import { PropsWithChildren, useMemo } from 'react';
import createSafeContext from '@paradigm/utils/src/createSafeContext';
import { isGrfq } from '#/unified-rfqs/entities/grfq/domain';
import { Rfq, RfqId } from '#/unified-rfqs/entities/rfq';
import { isDrfq } from '#/unified-rfqs/entities/drfq/domain';
import { useMaybeDrfqHistoryItem } from '#/unified-rfqs/contexts/drfqs';
import { Nullable } from '@paradigm/utils/types';
import { useObservable } from '@paradigm/utils/src/hooks';
import { useUnifiedRfqsService } from '../contexts/unified-rfqs';
import { useDrfqDraftsStore } from '../contexts/drfqs';

/**
 * **ATTENTION**: This hook should only be used in places the RFQ is guaranteed to exist.
 *
 * This hook tries to retrive an RFQ from all stores. If the rfq is stored
 * in `rfqsStore` or `rfqHistoryStore` it should return a valid RFQ.
 *
 * Otherwise it would throw an error. Because this hook should be used only when the RFQ is expected to exist.
 *
 * @returns Rfq
 */
export function useRfq() {
  const { rfq, rfqId } = useRfqContext();
  const drfqHistoryItem = useMaybeDrfqHistoryItem(rfqId as string);
  const storedDrfq = rfq || drfqHistoryItem;

  if (storedDrfq == null) {
    throw new TypeError(`Rfq with id ${rfqId} is unavailable.`);
  }

  return storedDrfq;
}

export function useGrfqRfq() {
  const rfq = useRfq();

  if (!isGrfq(rfq)) {
    throw new TypeError(`Rfq ${rfq.id} is not of kind grfq`);
  }

  return rfq;
}

export function useDrfqRfq() {
  const rfq = useRfq();

  if (!isDrfq(rfq)) {
    throw new TypeError(`Rfq ${rfq.id} is not of kind drfq`);
  }

  return rfq;
}

interface RfqState {
  rfq: Nullable<Rfq>;
  rfqId: Rfq['id'];
}

export const [useRfqContext, RfqProviderContext] =
  createSafeContext<RfqState>('RfqProvider');

export const useDrfqContext = () => {
  const { rfq, rfqId } = useRfqContext();
  if (rfq == null || !isDrfq(rfq)) return { rfq: null, rfqId };
  return { rfq, rfqId };
};

export const useGrfqContext = () => {
  const { rfq, rfqId } = useRfqContext();
  if (rfq == null || !isGrfq(rfq)) return { rfq: null, rfqId };
  return { rfq, rfqId };
};

/**
 * Fetches an RFQ from the unified store using the provided RFQ ID.
 * Note: Hook is private to discourage pattern of direct access to the store.
 *
 * @param {RfqId} rfqId - The RFQ ID to fetch data for.
 * @param {includeDrafts} includeDrafts - Whether to include RFQ Drafts
 * @returns {Nullable<Rfq>} - Maybe the RFQ.
 */
function _useMaybeRfq(rfqId: RfqId, includeDrafts = false): Nullable<Rfq> {
  const unifiedRfqsService = useUnifiedRfqsService();
  const draftDrfqStore = useDrfqDraftsStore();

  const serviceObs = useMemo(
    () => unifiedRfqsService.item(rfqId),
    [rfqId, unifiedRfqsService],
  );
  const draftObs = useMemo(
    () => draftDrfqStore.item(rfqId),
    [rfqId, draftDrfqStore],
  );

  const serviceRfq = useObservable(serviceObs);
  const draftRfq = useObservable(draftObs);

  if (includeDrafts) {
    return draftRfq ?? serviceRfq;
  }
  return serviceRfq;
}
interface RfqProviderProps {
  rfqId: RfqId;
  includeDrafts?: boolean;
}
export function RfqProvider(props: PropsWithChildren<RfqProviderProps>) {
  const { children, rfqId, includeDrafts } = props;
  const rfq = _useMaybeRfq(rfqId, includeDrafts);
  const state = useMemo(() => ({ rfq, rfqId }), [rfq, rfqId]);
  return <RfqProviderContext value={state}>{children}</RfqProviderContext>;
}
